import React, { useLayoutEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { ModalPortal } from '../modal/modal-portal';
import {
  bp,
  layout,
  palette,
  shadow,
  spacing,
  zIndex,
} from '../../style-utils/theme';
import { shortLandscape } from '../../style-utils/media-queries';

/* ═══════════ CLOCK IN AND THE LAST HANDOVER, PINNED, ON A PHONE ═══════════
 *
 * Owner decisions, 2026-09-11: "before clock in we must neatly display the
 * handover for the specific building always", then — shown that on a phone the
 * summary pushed Clock in below the screen — "pin Clock in" and "make sure both
 * clock in and the handover is visible".
 *
 * Measured before this existed (scripts/wp/verify-handover-devices.js), Clock
 * in's bottom edge sat at 727px on a 568px iPhone SE and 685px on a 664px
 * iPhone 13 with NO handover on the screen at all: below `md` the desk shell
 * stacks the building card and the "Start your shift" intro above the gate.
 * No amount of shrinking the summary could put both on screen from inside the
 * page's flow, so on a phone both leave it: a compact summary and the one
 * press, pinned just above the bottom tab bar.
 *
 * FIXED, SO IT IS PORTALED. The menu's entrance animation leaves a transform on
 * an ancestor, and a fixed element under a transform is positioned against that
 * ancestor rather than the viewport — the trap modal-portal.tsx documents.
 * `sticky` was not enough either: a sticky box cannot rise above its own
 * parent, and on the SE the whole gate card starts below the fold.
 *
 * TWO CSS VARIABLES ON <html>, both removed on unmount:
 *   --gate-dock-height  the dock's height. GateDockSpacer reserves it at the
 *                       end of the page, so nothing in the flow is left under
 *                       the dock.
 *   --gate-dock-lift    the same height, but ONLY when the dock's content
 *                       reaches into the IQ launcher's column. The launcher
 *                       (iq-dock.styled Fab) rises by it. On a phone held
 *                       sideways the content is centred at 560px and never
 *                       meets the launcher — lifting it there put it on top of
 *                       the notification bell (seen on iPhone 13 landscape).
 */

export const GATE_DOCK_VAR = '--gate-dock-height';
export const GATE_DOCK_LIFT_VAR = '--gate-dock-lift';

/* Where the dock is used: below `md`, which is exactly where the desk rail
   becomes a fixed bottom tab bar (sidebar.styled SidebarContainer). */
export const GATE_DOCK_QUERY = `(max-width: ${bp.md - 0.02}px)`;

/* The launcher's column, measured from the right edge: its widest inset
   (spacing.md, 24px) + its widest size (56px) + one clear step (8px). */
const LAUNCHER_COLUMN_PX = 88;

const Dock = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  /* Directly above the tab bar: its min-height plus the home-indicator
     padding it carries. NO BACKTICKS IN THESE COMMENTS. */
  bottom: calc(${layout.navHeight} + var(--safe-bottom));
  z-index: ${zIndex.sticky};
  padding: ${spacing.xs} ${spacing.sm};
  /* OPAQUE. The rail's frosted glass let the page's own text show through
     behind the summary lines — two sentences printed over each other. */
  background: ${palette.surface};
  border-top: 1px solid ${palette.line};
  box-shadow: ${shadow.lg};

  ${shortLandscape} {
    padding: ${spacing.half} ${spacing.sm};

    /* A phone on its side has ~340px for the bar, the dock and the page. The
       press keeps its full width and gives up height instead. */
    [data-clock-in] {
      min-height: 56px;
    }
  }
`;

/* A reading width, so a phone held sideways does not get a 700px press. */
const Inner = styled.div`
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;

  ${shortLandscape} {
    gap: ${spacing.half};
  }
`;

export const GateDock: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dockRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const dock = dockRef.current;
    const inner = innerRef.current;
    if (!dock || !inner) return undefined;
    const root = document.documentElement;
    const publish = () => {
      const height = `${Math.ceil(dock.getBoundingClientRect().height)}px`;
      const reachesLauncher =
        inner.getBoundingClientRect().right > window.innerWidth - LAUNCHER_COLUMN_PX;
      root.style.setProperty(GATE_DOCK_VAR, height);
      root.style.setProperty(GATE_DOCK_LIFT_VAR, reachesLauncher ? height : '0px');
    };

    publish();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(publish) : null;
    if (ro) ro.observe(dock);
    // Rotation changes the width without necessarily changing the dock's size.
    window.addEventListener('resize', publish);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', publish);
      root.style.removeProperty(GATE_DOCK_VAR);
      root.style.removeProperty(GATE_DOCK_LIFT_VAR);
    };
  }, []);

  return (
    <ModalPortal>
      <Dock ref={dockRef} data-gate-dock="">
        <Inner ref={innerRef}>{children}</Inner>
      </Dock>
    </ModalPortal>
  );
};

/* The same room at the end of the page's flow, so the last thing on the gate
   can always be scrolled clear of the dock. */
export const GateDockSpacer = styled.div`
  flex: none;
  height: var(${GATE_DOCK_VAR}, 0px);
`;
